import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { analyticsAPI } from '../../utils/api';

const Badge = ({ color, children }) => (
  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${color}`}>{children}</span>
);

const protocolColor = (p) => {
  switch (p) {
    case 'miracast': return 'bg-yellow-100 text-yellow-800';
    case 'dlna': return 'bg-green-100 text-green-800';
    case 'chromecast': return 'bg-blue-100 text-blue-800';
    case 'zcd': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const resultColor = (r) => {
  switch (r) {
    case 'success': return 'bg-green-100 text-green-800';
    case 'ok': return 'bg-green-100 text-green-800';
    case 'error': return 'bg-red-100 text-red-800';
    case 'user_cancelled': return 'bg-yellow-100 text-yellow-800';
    case 'sink_terminated': return 'bg-orange-100 text-orange-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const stepResultColor = (r) => {
  switch (r) {
    case 'ok': return 'text-green-600';
    case 'failed': return 'text-red-600';
    case 'timeout': return 'text-orange-600';
    case 'interrupted': return 'text-yellow-600';
    default: return 'text-gray-600';
  }
};

// ---------------------------------------------------------------------------
// Threshold helpers
// ---------------------------------------------------------------------------
// Each helper returns one of: 'green' | 'yellow' | 'red' | 'gray'
// 'gray' means "no signal" — field absent / null / not applicable.

const TONE_TEXT = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
  gray: 'text-gray-500',
};
const TONE_BG = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-600',
};
const TONE_BAR = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  gray: 'bg-gray-300',
};
const TONE_ICON = {
  green: '✓', // check
  yellow: '⚠', // warning sign
  red: '✗', // ballot x
  gray: '⊘', // circled slash
};

const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

const tConnect = (ms) => {
  if (ms == null) return 'red';
  if (ms < 3000) return 'green';
  if (ms <= 7000) return 'yellow';
  return 'red';
};
const tFirstFrame = (ms) => {
  if (ms == null) return 'red';
  if (ms < 2000) return 'green';
  if (ms <= 5000) return 'yellow';
  return 'red';
};
const tRssi = (dbm) => {
  if (!isNum(dbm) || dbm === 0) return 'gray';
  if (dbm > -65) return 'green';
  if (dbm >= -75) return 'yellow';
  return 'red';
};
const tLinkSpeed = (mbps) => {
  if (!isNum(mbps) || mbps === 0) return 'gray';
  if (mbps > 50) return 'green';
  if (mbps >= 25) return 'yellow';
  return 'red';
};
const tStallCount = (n) => {
  if (!isNum(n)) return 'gray';
  if (n === 0) return 'green';
  if (n <= 2) return 'yellow';
  return 'red';
};
const tStallTotal = (s) => {
  if (!isNum(s)) return 'gray';
  if (s === 0) return 'green';
  if (s <= 3) return 'yellow';
  return 'red';
};
const tRetry = (n) => {
  if (!isNum(n)) return 'gray';
  if (n === 0) return 'green';
  if (n === 1) return 'yellow';
  return 'red';
};
const tBatteryDrain = (pct) => {
  if (!isNum(pct)) return 'gray';
  if (pct < 5) return 'green';
  if (pct <= 15) return 'yellow';
  return 'red';
};
const tTempRise = (c) => {
  if (!isNum(c)) return 'gray';
  if (c < 5) return 'green';
  if (c <= 10) return 'yellow';
  return 'red';
};
const tNonZero = (n, yellowMax = 0) => {
  if (!isNum(n)) return 'gray';
  if (n === 0) return 'green';
  if (n <= yellowMax) return 'yellow';
  return 'red';
};

// Per-step thresholds — pick a (greenMax, yellowMax) tuple by step name.
const stepBudget = (name) => {
  const n = (name || '').toLowerCase();
  if (n.startsWith('rtsp_') || /\bm[1-7]\b/.test(n) || n.endsWith('_handshake') || n.includes('handshake')) {
    return [2000, 5000];
  }
  if (n === 'discovery' || n.startsWith('discover')) {
    return [5000, 15000];
  }
  if (n.includes('connect_p2p') || n.includes('wfd_connect') || n === 'p2p_connect') {
    return [3000, 8000];
  }
  if (n.includes('set_av_transport_uri') || n === 'setavtransporturi') {
    return [500, 2000];
  }
  return [1000, 5000];
};

const tStep = (step) => {
  const r = step?.result;
  if (r === 'failed' || r === 'timeout') return 'red';
  if (r === 'interrupted') return 'yellow';
  const dur = step?.duration_ms;
  if (!isNum(dur)) return r === 'ok' ? 'green' : 'gray';
  const [greenMax, yellowMax] = stepBudget(step.step);
  if (dur <= greenMax) return 'green';
  if (dur <= yellowMax) return 'yellow';
  return 'red';
};

// ---------------------------------------------------------------------------
// Health checklist builder
// ---------------------------------------------------------------------------
const ChecklistRow = ({ tone, children }) => (
  <div className="flex items-start gap-2 text-sm">
    <span className={`font-bold ${TONE_TEXT[tone]} w-4 flex-shrink-0 leading-5`}>{TONE_ICON[tone]}</span>
    <span className={tone === 'gray' ? 'text-gray-500' : 'text-gray-800'}>{children}</span>
  </div>
);

function buildChecklist({ session, sj, steps, outcome, connection, playback, battery, memory }) {
  const rows = [];
  const result = session.result;
  const isUserCancelled = result === 'user_cancelled';
  const isError = result === 'error';

  // Find last failed/timeout step + last good step
  let lastFailedIdx = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    const r = steps[i]?.result;
    if (r === 'failed' || r === 'timeout') { lastFailedIdx = i; break; }
  }
  let lastGoodStep = null;
  if (lastFailedIdx > 0) {
    for (let i = lastFailedIdx - 1; i >= 0; i--) {
      if (steps[i]?.result === 'ok') { lastGoodStep = steps[i]; break; }
    }
  } else if (lastFailedIdx === -1 && steps.length > 0) {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i]?.result === 'ok') { lastGoodStep = steps[i]; break; }
    }
  }

  // 1) Last good handshake / step result
  if (lastGoodStep) {
    rows.push({
      tone: tStep(lastGoodStep),
      text: `${lastGoodStep.step} completed${isNum(lastGoodStep.duration_ms) ? ` (${lastGoodStep.duration_ms} ms)` : ''}`,
    });
  } else if (steps.length > 0) {
    rows.push({ tone: 'gray', text: `No completed steps recorded` });
  }

  // 2) Connection summary
  const link = connection.wifi_link_speed_mbps;
  const rssi = connection.wifi_rssi_dbm;
  const band = connection.wifi_band;
  const linkTone = tLinkSpeed(link);
  const rssiTone = tRssi(rssi);
  let connTone = 'gray';
  if (linkTone !== 'gray' || rssiTone !== 'gray') {
    const tones = [linkTone, rssiTone].filter(t => t !== 'gray');
    if (tones.includes('red')) connTone = 'red';
    else if (tones.includes('yellow')) connTone = 'yellow';
    else connTone = 'green';
  }
  const connParts = [];
  if (isNum(link) && link > 0) connParts.push(`${link} Mbps`);
  if (isNum(rssi) && rssi !== 0) connParts.push(`${rssi} dBm`);
  if (band) connParts.push(band);
  if (connParts.length > 0) {
    rows.push({ tone: connTone, text: `Connection: ${connParts.join(' · ')}` });
  } else {
    rows.push({ tone: 'gray', text: `Connection: no WiFi telemetry` });
  }

  // 3) Connect time
  if (isNum(session.connect_time_ms)) {
    const tone = tConnect(session.connect_time_ms);
    rows.push({ tone, text: `Connect time: ${session.connect_time_ms} ms` });
  }

  // 4) First frame
  const ff = session.first_frame_time_ms;
  if (isNum(ff)) {
    const tone = tFirstFrame(ff);
    rows.push({ tone, text: `First frame at ${ff} ms` });
  } else if (isUserCancelled) {
    rows.push({ tone: 'yellow', text: 'User cancelled before first frame was captured' });
  } else if (isError) {
    rows.push({ tone: 'red', text: 'First frame never observed' });
  } else {
    rows.push({ tone: 'gray', text: 'First frame: no telemetry' });
  }

  // 5) Failure summary
  if (isError) {
    const stage = outcome.error_stage || '—';
    const code = outcome.error_code || 'unknown';
    rows.push({
      tone: 'red',
      text: `Session ended at stage=${stage} with error ${code}`,
    });
  } else if (result === 'sink_terminated') {
    rows.push({ tone: 'yellow', text: 'Sink terminated the session' });
  }

  // 6) Stalls / pauses / seeks
  const stalls = playback.buffer_stall_count;
  const stallSec = playback.buffer_stall_total_s;
  const pauses = playback.pause_count;
  const seeks = playback.seek_count;
  const playbackKnown = [stalls, stallSec, pauses, seeks].some(isNum);
  if (playbackKnown) {
    const tones = [tStallCount(stalls), tStallTotal(stallSec), tNonZero(pauses, 0), tNonZero(seeks, 0)];
    let pTone;
    if (tones.includes('red')) pTone = 'red';
    else if (tones.includes('yellow')) pTone = 'yellow';
    else pTone = 'green';
    if (pTone === 'green') {
      rows.push({ tone: 'green', text: 'No stalls / pauses / seeks' });
    } else {
      const issues = [];
      if (isNum(stalls) && stalls > 0) issues.push(`${stalls} stall${stalls === 1 ? '' : 's'}${isNum(stallSec) && stallSec > 0 ? ` (${stallSec}s)` : ''}`);
      if (isNum(pauses) && pauses > 0) issues.push(`${pauses} pause${pauses === 1 ? '' : 's'}`);
      if (isNum(seeks) && seeks > 0) issues.push(`${seeks} seek${seeks === 1 ? '' : 's'}`);
      rows.push({ tone: pTone, text: `Playback: ${issues.join(', ') || 'see metrics'}` });
    }
  }

  // 7) Retries
  if (isNum(connection.retry_count) && connection.retry_count > 0) {
    rows.push({
      tone: tRetry(connection.retry_count),
      text: `${connection.retry_count} retr${connection.retry_count === 1 ? 'y' : 'ies'} during connect`,
    });
  }

  // 8) Battery / temp / RAM
  let batteryDrain = null;
  if (isNum(battery.level_start_pct) && isNum(battery.level_end_pct)) {
    batteryDrain = battery.level_start_pct - battery.level_end_pct;
  }
  let tempRise = null;
  if (isNum(battery.temperature_start_c) && isNum(battery.temperature_end_c) &&
      battery.temperature_start_c > 0 && battery.temperature_end_c > 0) {
    tempRise = battery.temperature_end_c - battery.temperature_start_c;
  }
  if (batteryDrain != null || tempRise != null) {
    const bTone = tBatteryDrain(batteryDrain);
    const tTone = tTempRise(tempRise);
    const tones = [bTone, tTone].filter(t => t !== 'gray');
    let combined;
    if (tones.includes('red')) combined = 'red';
    else if (tones.includes('yellow')) combined = 'yellow';
    else combined = tones.length === 0 ? 'gray' : 'green';
    if (combined === 'green') {
      rows.push({ tone: 'green', text: 'Battery and temperature stable' });
    } else {
      const parts = [];
      if (batteryDrain != null) parts.push(`battery ${batteryDrain >= 0 ? '-' : '+'}${Math.abs(batteryDrain)}%`);
      if (tempRise != null) parts.push(`temp ${tempRise >= 0 ? '+' : ''}${tempRise.toFixed(1)}°C`);
      rows.push({ tone: combined, text: `Device load: ${parts.join(', ')}` });
    }
  }

  return rows;
}

function overallVerdict({ session, rows }) {
  if (session.result === 'error') return 'red';
  // Red row → degraded for non-error sessions, but cap at yellow.
  if (rows.some(r => r.tone === 'red')) return 'yellow';
  if (rows.some(r => r.tone === 'yellow')) return 'yellow';
  return 'green';
}

const VERDICT_LABEL = {
  green: { icon: '✓', text: 'HEALTHY', wrap: 'border-green-300 bg-green-50', label: 'text-green-700' },
  yellow: { icon: '⚠', text: 'DEGRADED', wrap: 'border-yellow-300 bg-yellow-50', label: 'text-yellow-800' },
  red: { icon: '✗', text: 'FAILED', wrap: 'border-red-300 bg-red-50', label: 'text-red-700' },
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SessionDetailPage() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.getSession(id).then(res => {
      setSession(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-gray-400 p-8">Loading session...</div>;
  if (!session) return <div className="text-red-400 p-8">Session not found</div>;

  const sj = typeof session.session_json === 'string' ? JSON.parse(session.session_json) : (session.session_json || {});
  const steps = sj.steps || [];
  const sink = sj.sink_device || {};
  const outcome = sj.outcome || {};
  const connection = sj.connection || {};
  const playback = sj.playback || {};
  const battery = sj.battery || {};
  const memory = sj.memory || {};
  const pipeline = sj.pipeline || {};
  const sourceContent = sj.source_content || {};

  const miracastInfo = sink.miracast_info;
  const dlnaInfo = sink.dlna_info;
  const chromecastInfo = sink.chromecast_info;
  const zcdInfo = sink.zcd_info;

  const fmt = (ms) => ms != null ? `${ms}ms` : '—';
  const fmtTime = (ts) => ts ? new Date(ts).toLocaleString() : '—';

  // ----- threshold-driven derived values -----
  const connectTone = tConnect(session.connect_time_ms);
  const firstFrameTone = session.result === 'user_cancelled' && session.first_frame_time_ms == null
    ? 'gray'
    : tFirstFrame(session.first_frame_time_ms);
  const rssiTone = tRssi(connection.wifi_rssi_dbm);
  const linkTone = tLinkSpeed(connection.wifi_link_speed_mbps);
  const retryTone = tRetry(connection.retry_count);
  const stallCountTone = tStallCount(playback.buffer_stall_count);
  const stallTotalTone = tStallTotal(playback.buffer_stall_total_s);
  const pauseTone = tNonZero(playback.pause_count, 0);
  const seekTone = tNonZero(playback.seek_count, 0);

  let batteryDrain = null;
  if (isNum(battery.level_start_pct) && isNum(battery.level_end_pct)) {
    batteryDrain = battery.level_start_pct - battery.level_end_pct;
  }
  const batteryTone = tBatteryDrain(batteryDrain);
  let tempRise = null;
  if (isNum(battery.temperature_start_c) && isNum(battery.temperature_end_c) &&
      battery.temperature_start_c > 0 && battery.temperature_end_c > 0) {
    tempRise = battery.temperature_end_c - battery.temperature_start_c;
  }
  const tempTone = tTempRise(tempRise);

  // checklist + verdict
  const checklist = buildChecklist({ session, sj, steps, outcome, connection, playback, battery, memory });
  const verdict = overallVerdict({ session, rows: checklist });
  const v = VERDICT_LABEL[verdict];

  // "Where it failed" data
  let failedStep = null;
  let failedStepIdx = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    const r = steps[i]?.result;
    if (r === 'failed' || r === 'timeout') {
      failedStep = steps[i];
      failedStepIdx = i;
      break;
    }
  }
  let lastGoodStep = null;
  if (failedStepIdx > 0) {
    for (let i = failedStepIdx - 1; i >= 0; i--) {
      if (steps[i]?.result === 'ok') { lastGoodStep = steps[i]; break; }
    }
  } else if (failedStepIdx === -1) {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i]?.result === 'ok') { lastGoodStep = steps[i]; break; }
    }
  }

  return (
    <div className="max-w-5xl mx-auto">
      <Link to="/admin/casting" className="text-zebcast-purple hover:underline text-sm mb-4 inline-block">&larr; Back to Sessions</Link>

      <div className="flex items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Session Detail</h2>
        <Badge color={protocolColor(session.protocol)}>{(session.protocol || 'unknown').toUpperCase()}</Badge>
        <Badge color={resultColor(session.result)}>{session.result}</Badge>
      </div>

      {/* Session Health Panel */}
      <div className={`border-2 ${v.wrap} rounded-lg p-5 mb-6`}>
        <div className="flex items-center gap-3 mb-4">
          <span className={`text-2xl font-bold ${v.label}`}>{v.icon}</span>
          <span className={`text-xl font-bold ${v.label}`}>{v.text}</span>
          <span className="text-xs text-gray-500 ml-auto">Session Health</span>
        </div>
        <div className="space-y-1.5">
          {checklist.map((row, i) => (
            <ChecklistRow key={i} tone={row.tone}>{row.text}</ChecklistRow>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase">Sink Device</p>
          <p className="text-lg font-bold text-gray-900">{session.sink_name || 'Local'}</p>
          <p className="text-xs text-gray-500">{sink.device_type} {sink.resolved_identity ? `(${sink.resolved_identity})` : ''}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase">Connect Time</p>
          <p className={`text-lg font-bold ${TONE_TEXT[connectTone]}`}>{fmt(session.connect_time_ms)}</p>
          <p className="text-xs text-gray-500">First frame: <span className={TONE_TEXT[firstFrameTone]}>{fmt(session.first_frame_time_ms)}</span></p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase">Play Time</p>
          <p className="text-lg font-bold text-gray-900">{session.total_play_time_s || 0}s</p>
          <p className="text-xs text-gray-500">{session.percentage_watched ? `${session.percentage_watched.toFixed(1)}% watched` : ''}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase">WiFi</p>
          <p className="text-lg font-bold text-gray-900">{connection.wifi_band || '—'}</p>
          <p className="text-xs text-gray-500">
            {connection.wifi_frequency_mhz ? `${connection.wifi_frequency_mhz} MHz ` : ''}
            {connection.wifi_rssi_dbm ? <span className={TONE_TEXT[rssiTone]}>{`${connection.wifi_rssi_dbm} dBm`}</span> : ''}
          </p>
        </div>
      </div>

      {/* Error Section */}
      {outcome.error_code && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-2">
          <h3 className="text-red-800 font-bold mb-1">Error: {outcome.error_code}</h3>
          <p className="text-red-700 text-sm">
            Stage: <strong>{outcome.error_stage || '—'}</strong> | Domain: <strong>{outcome.error_domain || '—'}</strong>
          </p>
          {outcome.error_detail && <p className="text-red-600 text-sm mt-1">{outcome.error_detail}</p>}
          <p className="text-red-500 text-xs mt-1">End reason: {outcome.end_reason}</p>
          {outcome.error_code === 'STORAGE_ACCESS_DENIED' && (
            <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3">
              <p className="text-orange-800 text-sm font-semibold">Storage Permission Issue</p>
              <p className="text-orange-700 text-xs mt-1">
                This device could not read the media file — likely an SD card on Android 13+ where
                MANAGE_EXTERNAL_STORAGE is not granted. The app has been updated to fall back to
                file descriptor access (fd://) for these cases.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Where it failed */}
      {(outcome.error_code || failedStep) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-bold text-gray-800 mb-2">Where it failed</p>
          <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-sm font-mono">
            <dt className="text-gray-500">Stage:</dt>
            <dd className="text-gray-800">{outcome.error_stage || '—'}</dd>
            <dt className="text-gray-500">At step:</dt>
            <dd className="text-gray-800">
              {failedStep
                ? `${failedStep.step}${isNum(failedStep.duration_ms) ? ` (took ${(failedStep.duration_ms / 1000).toFixed(1)}s before ${failedStep.result})` : ''}`
                : '—'}
            </dd>
            <dt className="text-gray-500">Last good:</dt>
            <dd className="text-gray-800">
              {lastGoodStep
                ? `${lastGoodStep.step}${isNum(lastGoodStep.duration_ms) ? ` (${lastGoodStep.duration_ms} ms)` : ''}`
                : '—'}
            </dd>
            <dt className="text-gray-500">Error:</dt>
            <dd className="text-gray-800">
              {outcome.error_code
                ? `${outcome.error_code}${outcome.error_detail ? ` — ${outcome.error_detail}` : ''}`
                : '—'}
            </dd>
          </dl>
        </div>
      )}

      {/* Step Timeline */}
      {steps.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Step Timeline ({steps.length} steps)</h3>
          <div className="space-y-2">
            {steps.map((step, i) => {
              const tone = tStep(step);
              const slow = tone === 'yellow' || tone === 'red';
              return (
                <div key={i} className="flex items-stretch gap-3 py-2 border-b border-gray-100 last:border-0">
                  <div className={`w-1 rounded-sm ${TONE_BAR[tone]}`}></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0 self-center">{i + 1}</div>
                  <div className="flex-1 self-center">
                    <p className="font-mono text-sm font-semibold text-gray-800">
                      {step.step}
                      {slow && step.result !== 'failed' && step.result !== 'timeout' && (
                        <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TONE_BG[tone]}`}>SLOW</span>
                      )}
                    </p>
                    {step.detail && <p className="text-xs text-gray-500 truncate max-w-lg">{step.detail}</p>}
                  </div>
                  <div className="text-right self-center">
                    <p className={`text-sm font-bold ${stepResultColor(step.result)}`}>{step.result || '—'}</p>
                    <p className={`text-base font-mono font-semibold ${TONE_TEXT[tone]}`}>{step.duration_ms != null ? `${step.duration_ms}ms` : '—'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Miracast P2P Connection Chain */}
      {miracastInfo && miracastInfo.p2p_connection_chain && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Miracast P2P Connection Chain</h3>
          <p className="text-sm text-gray-500 mb-4">
            {miracastInfo.total_p2p_attempts} attempts | Final strategy: <strong>{miracastInfo.final_strategy}</strong> | Device: {miracastInfo.p2p_device_name}
            {miracastInfo.wifi_disconnected_for_p2p && <span className="ml-2 text-orange-600 font-semibold">(WiFi disconnected for P2P)</span>}
          </p>
          {/* Channel Diagnostics */}
          {(miracastInfo.sta_frequency_mhz || miracastInfo.p2p_group_frequency_mhz) && (
            <div className={`p-3 rounded-lg border mb-4 ${miracastInfo.p2p_group_is_dfs ? 'border-orange-300 bg-orange-50' : 'border-blue-200 bg-blue-50'}`}>
              <p className="text-sm font-semibold text-gray-800 mb-2">Channel Diagnostics</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {miracastInfo.sta_frequency_mhz > 0 && (
                  <div>
                    <span className="text-gray-500">STA:</span>{' '}
                    <strong>{miracastInfo.sta_frequency_mhz} MHz</strong>
                    <span className="text-gray-400 ml-1">(ch{miracastInfo.sta_channel}, {miracastInfo.sta_band})</span>
                    {miracastInfo.sta_is_dfs && <span className="ml-1 text-orange-600 font-bold">DFS</span>}
                  </div>
                )}
                {miracastInfo.p2p_group_frequency_mhz > 0 && (
                  <div>
                    <span className="text-gray-500">P2P Group:</span>{' '}
                    <strong>{miracastInfo.p2p_group_frequency_mhz} MHz</strong>
                    <span className="text-gray-400 ml-1">(ch{miracastInfo.p2p_group_channel}, {miracastInfo.p2p_group_band})</span>
                    {miracastInfo.p2p_group_is_dfs && <span className="ml-1 text-orange-600 font-bold">DFS</span>}
                  </div>
                )}
                {miracastInfo.sta_and_group_same_channel != null && (
                  <div>
                    <span className="text-gray-500">Same channel:</span>{' '}
                    <strong>{miracastInfo.sta_and_group_same_channel ? 'Yes (SCC)' : 'No'}</strong>
                  </div>
                )}
              </div>
              {miracastInfo.p2p_group_is_dfs && (
                <p className="text-orange-700 text-xs mt-2">
                  P2P group on DFS channel — many Miracast sinks refuse to join DFS channels for P2P client operation.
                  SCC chip locked the group to the STA channel. Try switching WiFi to a non-DFS channel (36-48).
                </p>
              )}
            </div>
          )}
          <div className="space-y-3">
            {miracastInfo.p2p_connection_chain.map((attempt, i) => (
              <div key={i} className={`p-3 rounded-lg border ${attempt.result === 'success' ? 'border-green-200 bg-green-50' : attempt.result === 'timeout' ? 'border-orange-200 bg-orange-50' : 'border-red-200 bg-red-50'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-mono text-sm font-bold">Attempt {attempt.attempt || i + 1}: {attempt.strategy}</span>
                    {attempt.failure_reason && <span className="text-sm text-red-600 ml-2">— {attempt.failure_reason}</span>}
                  </div>
                  <div className="text-right">
                    <Badge color={attempt.result === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{attempt.result}</Badge>
                    <p className="text-xs text-gray-500 mt-1">{attempt.duration_ms}ms</p>
                  </div>
                </div>
                {attempt.group_frequency_mhz && (
                  <p className="text-xs text-gray-500 mt-1">Group: {attempt.group_frequency_mhz} MHz ({attempt.group_band})</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chromecast Info */}
      {chromecastInfo && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Chromecast Info</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Model:</span> <strong>{chromecastInfo.cast_model}</strong></div>
            <div><span className="text-gray-500">Generation:</span> <strong>{chromecastInfo.cast_generation}</strong></div>
            <div><span className="text-gray-500">Android-based:</span> <strong>{chromecastInfo.is_android_based ? 'Yes' : 'No'}</strong></div>
            <div><span className="text-gray-500">Receiver:</span> <strong>{chromecastInfo.receiver_type}</strong></div>
          </div>
        </div>
      )}

      {/* ZCD Info */}
      {zcdInfo && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">ZCD Receiver Info</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {zcdInfo.platform && <div><span className="text-gray-500">Platform:</span> <strong>{zcdInfo.platform}</strong></div>}
            {zcdInfo.firmware_version && <div><span className="text-gray-500">Firmware:</span> <strong>{zcdInfo.firmware_version}</strong></div>}
            {zcdInfo.protocol_version && <div><span className="text-gray-500">Protocol:</span> <strong>{zcdInfo.protocol_version}</strong></div>}
          </div>
        </div>
      )}

      {/* DLNA Info */}
      {dlnaInfo && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">DLNA Info</h3>
          {dlnaInfo.supported_protocols && (
            <div className="text-sm"><span className="text-gray-500">Protocols:</span> <code className="text-xs bg-gray-100 p-1 rounded">{dlnaInfo.supported_protocols.substring(0, 200)}</code></div>
          )}
        </div>
      )}

      {/* Source Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Source Content</h3>
          <div className="space-y-1 text-sm">
            <div><span className="text-gray-500">Origin:</span> <strong>{sourceContent.origin}</strong></div>
            <div><span className="text-gray-500">Type:</span> <strong>{sourceContent.media_type}</strong></div>
            {sourceContent.container_format && <div><span className="text-gray-500">Container:</span> <strong>{sourceContent.container_format}</strong></div>}
            {sourceContent.video && (
              <>
                <div><span className="text-gray-500">Video:</span> <strong>{sourceContent.video.codec} {sourceContent.video.resolution} {sourceContent.video.framerate ? `@${sourceContent.video.framerate}fps` : ''}</strong></div>
                {sourceContent.video.bitrate_bps > 0 && <div><span className="text-gray-500">Bitrate:</span> <strong>{Math.round(sourceContent.video.bitrate_bps / 1000)}kbps</strong></div>}
              </>
            )}
            {sourceContent.audio && (
              <div><span className="text-gray-500">Audio:</span> <strong>{sourceContent.audio.codec} {sourceContent.audio.channels}ch {sourceContent.audio.sample_rate ? `${sourceContent.audio.sample_rate}Hz` : ''}</strong></div>
            )}
            {sourceContent.duration_seconds > 0 && <div><span className="text-gray-500">Duration:</span> <strong>{Math.floor(sourceContent.duration_seconds / 60)}m {sourceContent.duration_seconds % 60}s</strong></div>}
            {sourceContent.file_size_bytes > 0 && <div><span className="text-gray-500">Size:</span> <strong>{(sourceContent.file_size_bytes / 1048576).toFixed(1)} MB</strong></div>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Pipeline & Playback</h3>
          <div className="space-y-1 text-sm">
            {pipeline.pipeline_path && <div><span className="text-gray-500">Path:</span> <strong>{pipeline.pipeline_path}</strong></div>}
            {pipeline.encoder_used && <div><span className="text-gray-500">Encoder:</span> <strong>{pipeline.encoder_used}</strong></div>}
            {pipeline.transcode_decision && <div><span className="text-gray-500">Transcode:</span> <strong>{pipeline.transcode_decision}</strong></div>}
            <div className="pt-2 border-t border-gray-100 mt-2">
              <div>
                <span className="text-gray-500">Pauses:</span>{' '}
                <strong className={TONE_TEXT[pauseTone]}>{playback.pause_count || 0}</strong>{' | '}
                <span className="text-gray-500">Seeks:</span>{' '}
                <strong className={TONE_TEXT[seekTone]}>{playback.seek_count || 0}</strong>{' | '}
                <span className="text-gray-500">Stalls:</span>{' '}
                <strong className={TONE_TEXT[stallCountTone]}>{playback.buffer_stall_count || 0}</strong>
              </div>
              {playback.buffer_stall_total_s > 0 && (
                <div>
                  <span className="text-gray-500">Total stall:</span>{' '}
                  <strong className={TONE_TEXT[stallTotalTone]}>{playback.buffer_stall_total_s}s</strong>{' '}
                  (max {playback.max_buffer_stall_s}s)
                </div>
              )}
            </div>
            <div className="pt-2 border-t border-gray-100 mt-2">
              {battery.level_start_pct >= 0 && (
                <div>
                  <span className="text-gray-500">Battery:</span>{' '}
                  <strong>▼ {battery.level_start_pct}% → {battery.level_end_pct}%</strong>{' '}
                  {batteryDrain != null && (
                    <span className={`font-semibold ${TONE_TEXT[batteryTone]}`}>({batteryDrain >= 0 ? '-' : '+'}{Math.abs(batteryDrain)}%)</span>
                  )}{' '}
                  {battery.is_charging_start ? '(charging)' : ''}
                </div>
              )}
              {battery.temperature_start_c > 0 && (
                <div>
                  <span className="text-gray-500">Temp:</span>{' '}
                  <strong>{battery.temperature_start_c}°C → {battery.temperature_end_c}°C</strong>{' '}
                  {tempRise != null && (
                    <span className={`font-semibold ${TONE_TEXT[tempTone]}`}>({tempRise >= 0 ? '+' : ''}{tempRise.toFixed(1)}°C)</span>
                  )}
                </div>
              )}
              {memory.available_ram_mb_start > 0 && <div><span className="text-gray-500">RAM:</span> <strong>{memory.available_ram_mb_start}MB → {memory.available_ram_mb_end}MB</strong></div>}
            </div>
          </div>
        </div>
      </div>

      {/* Connection Details */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Connection Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-gray-500">Type:</span> <strong>{connection.connection_type || '—'}</strong></div>
          <div><span className="text-gray-500">WiFi Band:</span> <strong>{connection.wifi_band || '—'}</strong></div>
          <div><span className="text-gray-500">Frequency:</span> <strong>{connection.wifi_frequency_mhz || '—'} MHz</strong></div>
          <div><span className="text-gray-500">RSSI:</span> <strong className={TONE_TEXT[rssiTone]}>{connection.wifi_rssi_dbm || '—'} dBm</strong></div>
          <div><span className="text-gray-500">Link Speed:</span> <strong className={TONE_TEXT[linkTone]}>{connection.wifi_link_speed_mbps || '—'} Mbps</strong></div>
          <div><span className="text-gray-500">Retries:</span> <strong className={TONE_TEXT[retryTone]}>{connection.retry_count || 0}</strong></div>
          {connection.discovery_time_ms > 0 && <div><span className="text-gray-500">Discovery:</span> <strong>{connection.discovery_time_ms}ms</strong></div>}
        </div>
        {connection.retry_reasons && connection.retry_reasons.length > 0 && (
          <div className="mt-2 text-sm"><span className="text-gray-500">Retry reasons:</span> {connection.retry_reasons.join(', ')}</div>
        )}
      </div>

      {/* Timestamps */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Timestamps</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-500">Session ID:</span> <code className="text-xs bg-gray-100 p-1 rounded">{session.session_id}</code></div>
          <div><span className="text-gray-500">Device ID:</span> <code className="text-xs bg-gray-100 p-1 rounded">{session.device_id}</code></div>
          <div><span className="text-gray-500">Start:</span> <strong>{fmtTime(session.timestamp_start)}</strong></div>
          <div><span className="text-gray-500">End:</span> <strong>{fmtTime(session.timestamp_end)}</strong></div>
        </div>
      </div>

      {/* Playback verdict — answers "did this cast actually work?".
          Computed by SessionObserver from HTTP request signature
          (DLNA/CC/HLS) or RTSP M-phase + RTP flow + M16 keepalives
          (Miracast). Only present on v1.11.20.10+. */}
      {sj.playback_verdict && (
        <PlaybackVerdictPanel verdict={sj.playback_verdict} observer={sj.observer} />
      )}

      {/* Transaction log — per-protocol low-level events captured during
          the session (RTSP M-messages, DLNA SOAP, CC load, transcode
          decisions). Only present on devices running v1.11.20.8+. */}
      {Array.isArray(sj.transaction_log) && sj.transaction_log.length > 0 && (
        <>
          <AdvertisedVsSetPanel entries={sj.transaction_log} />
          <TransactionLogPanel entries={sj.transaction_log} sessionStart={sj.timestamp_start} />
        </>
      )}

      {/* Raw JSON */}
      <details className="bg-white rounded-lg shadow p-6 mb-6">
        <summary className="text-lg font-bold text-gray-900 cursor-pointer">Raw Session JSON</summary>
        <pre className="mt-4 bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs max-h-96">
          {JSON.stringify(sj, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// PlaybackVerdictPanel — top-level "did the cast actually work?" answer.
// Driven by SessionObserver's analysis of HTTP request burst pattern
// (DLNA/CC/HLS) or RTSP M-phase + RTP flow + M16 keepalives (Miracast).
//
// Verdicts (from SessionObserver.Verdict):
//   PLAYED_THROUGH        — green; full playback observed
//   PLAYED_PARTIAL        — green/yellow; played but ended early
//   PLAYED_WITH_SEEKS     — green; sequential blocks with discrete jumps
//   PROBE_ONLY            — red; bursty short-lived requests, no playback
//   SILENT_FAILURE        — red; connection up, no data flowed
//   NEVER_CONNECTED       — gray; sink never reached our server
//   STALL_BEFORE_PLAY     — red; Miracast negotiation hung pre-M7
// ────────────────────────────────────────────────────────────────────────
function PlaybackVerdictPanel({ verdict, observer }) {
  const v = verdict?.verdict;
  const human = verdict?.verdict_human;
  const sig = verdict?.signature || {};
  const httpReqs = observer?.http_requests || [];

  const tone =
    v === 'PLAYED_THROUGH'    ? { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900',  pill: 'bg-green-600 text-white',  emoji: '✓' } :
    v === 'PLAYED_PARTIAL'    ? { bg: 'bg-lime-50',  border: 'border-lime-300',  text: 'text-lime-900',   pill: 'bg-lime-600 text-white',   emoji: '✓' } :
    v === 'PLAYED_WITH_SEEKS' ? { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-900',  pill: 'bg-green-600 text-white',  emoji: '✓' } :
    v === 'PROBE_ONLY'        ? { bg: 'bg-red-50',   border: 'border-red-300',   text: 'text-red-900',    pill: 'bg-red-600 text-white',    emoji: '✗' } :
    v === 'SILENT_FAILURE'    ? { bg: 'bg-red-50',   border: 'border-red-300',   text: 'text-red-900',    pill: 'bg-red-600 text-white',    emoji: '✗' } :
    v === 'STALL_BEFORE_PLAY' ? { bg: 'bg-red-50',   border: 'border-red-300',   text: 'text-red-900',    pill: 'bg-red-600 text-white',    emoji: '✗' } :
    v === 'NEVER_CONNECTED'   ? { bg: 'bg-gray-50',  border: 'border-gray-300',  text: 'text-gray-700',   pill: 'bg-gray-500 text-white',   emoji: '?' } :
                                { bg: 'bg-gray-50',  border: 'border-gray-300',  text: 'text-gray-700',   pill: 'bg-gray-500 text-white',   emoji: '?' };

  return (
    <div className={`rounded-lg shadow-md p-6 mb-6 border-2 ${tone.bg} ${tone.border}`}>
      <div className="flex items-start gap-4">
        <div className={`text-4xl font-black ${tone.text}`}>{tone.emoji}</div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-lg font-bold text-gray-900">Playback verdict</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${tone.pill}`}>{v}</span>
          </div>
          <p className={`${tone.text} text-base mb-3`}>{human}</p>

          {/* Signature — the data backing the verdict */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-2">
            {sig.http_request_count != null && (
              <Sig label="HTTP requests" value={sig.http_request_count} />
            )}
            {sig.http_bytes_served != null && (
              <Sig label="Bytes served" value={`${(sig.http_bytes_served / (1024 * 1024)).toFixed(1)} MB`} />
            )}
            {sig.expected_media_bytes > 0 && (
              <Sig label="Expected size" value={`${(sig.expected_media_bytes / (1024 * 1024)).toFixed(1)} MB`} />
            )}
            {sig.pct_of_file_served != null && (
              <Sig label="% of file" value={`${sig.pct_of_file_served}%`} />
            )}
            {sig.has_long_lived_request != null && (
              <Sig label="Long-lived req?" value={sig.has_long_lived_request ? 'yes' : 'no'} />
            )}
            {sig.monotonic_steps != null && (
              <Sig label="Monotonic steps" value={sig.monotonic_steps} />
            )}
            {sig.rewinds != null && sig.rewinds > 0 && (
              <Sig label="Rewinds (seeks)" value={sig.rewinds} />
            )}
            {sig.max_m_phase != null && (
              <Sig label="Max M-phase" value={`M${sig.max_m_phase}`} />
            )}
            {sig.m16_keepalives != null && (
              <Sig label="M16 keepalives" value={sig.m16_keepalives} />
            )}
            {sig.rtp_packets_sent != null && sig.rtp_packets_sent > 0 && (
              <Sig label="RTP packets" value={sig.rtp_packets_sent} />
            )}
            {sig.vlc_reached_playing != null && (
              <Sig label="VLC playing?" value={sig.vlc_reached_playing ? 'yes' : 'no'} />
            )}
            {sig.vlc_encountered_error != null && sig.vlc_encountered_error && (
              <Sig label="VLC error" value="yes" tone="red" />
            )}
            {verdict.session_duration_ms != null && (
              <Sig label="Duration" value={`${(verdict.session_duration_ms / 1000).toFixed(1)}s`} />
            )}
            {sig.avg_request_spacing_ms != null && sig.avg_request_spacing_ms > 0 && (
              <Sig label="Avg req spacing" value={`${sig.avg_request_spacing_ms}ms`} />
            )}
          </div>

          {/* HTTP request log — collapsible inline detail */}
          {httpReqs.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-gray-700 cursor-pointer hover:text-gray-900 font-medium">
                HTTP request signature ({httpReqs.length} requests)
              </summary>
              <div className="mt-2 bg-white rounded border p-2 max-h-64 overflow-y-auto">
                <table className="text-[11px] font-mono w-full">
                  <thead>
                    <tr className="text-gray-500 border-b">
                      <th className="text-left px-1">+t</th>
                      <th className="text-left px-1">method</th>
                      <th className="text-left px-1">path</th>
                      <th className="text-right px-1">range</th>
                      <th className="text-right px-1">bytes</th>
                      <th className="text-right px-1">dur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {httpReqs.map((r, i) => {
                      const baseTs = httpReqs[0].ts;
                      const rel = ((r.ts - baseTs) / 1000).toFixed(1);
                      return (
                        <tr key={i} className="border-b">
                          <td className="px-1 text-gray-500">+{rel}s</td>
                          <td className="px-1">{r.method}</td>
                          <td className="px-1 truncate max-w-xs" title={r.path}>{r.path}</td>
                          <td className="px-1 text-right text-gray-600">
                            {r.range_start != null
                              ? `${r.range_start}${r.range_end != null ? `-${r.range_end}` : '+'}`
                              : '—'}
                          </td>
                          <td className="px-1 text-right">{r.bytes_served || 0}</td>
                          <td className="px-1 text-right">{r.duration_ms || 0}ms</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function Sig({ label, value, tone }) {
  const valueClass = tone === 'red' ? 'text-red-700 font-bold' : 'text-gray-900 font-medium';
  return (
    <div className="bg-white/60 rounded px-2 py-1">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`${valueClass} mt-0.5`}>{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// AdvertisedVsSetPanel — extracts the high-value "what TV said" vs "what
// zebcast set" pairs from the transaction log and renders them side by
// side. This is the question the user keeps asking ("the TV advertised
// what, zebcast set what?") so it gets top billing above the raw log.
//
// Sources:
//   DLNA:     dlna_soap GetProtocolInfo (advertised) vs SetAVTransportURI (set)
//   Miracast: wfd_rtsp GET_PARAMETER (advertised codecs) vs wfd_streamer
//             session_ready (negotiated formats)
//   CC:       cc_load load_request (MIME we declared) vs load_response (TV verdict)
//   Transcode: transcode_decision encoder_chosen + transcode_progress
//              completed/error
// ────────────────────────────────────────────────────────────────────────
function AdvertisedVsSetPanel({ entries }) {
  // Index by layer+type for quick lookup
  const find = (layer, type) => entries.find(e => e.layer === layer && e.type === type);

  // ---- DLNA ----
  const dlnaProtoInfo = find('dlna_soap', 'GetProtocolInfo');
  const dlnaSetUri    = find('dlna_soap', 'SetAVTransportURI');
  const dlnaSetUriResp = find('dlna_soap', 'SetAVTransportURI_response');

  // ---- Miracast (request/response pairs grouped by m_phase) ----
  const wfdEvents = entries.filter(e => e.layer === 'wfd_rtsp' && e.props?.m_phase);
  const wfdGetParam = wfdEvents.find(e => e.props.m_phase === 'M3' && e.props.direction === 'in');
  const wfdSetParamCap = wfdEvents.find(e => e.props.m_phase === 'M4' && e.props.direction === 'in');
  const wfdSessionReady = find('wfd_streamer', 'session_ready');

  // ---- CC ----
  const ccLoad = find('cc_load', 'load_request');
  const ccResp = find('cc_load', 'load_response');

  // ---- Transcode ----
  const tcDecision = find('transcode_decision', 'encoder_chosen');
  const tcCompleted = find('transcode_progress', 'completed');
  const tcError = find('transcode_progress', 'error');

  const hasAny = dlnaProtoInfo || wfdGetParam || ccLoad || tcDecision;
  if (!hasAny) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-bold text-gray-900 mb-1">TV advertised vs zebcast set</h3>
      <p className="text-xs text-gray-500 mb-4">
        Side-by-side diff of the sink's claimed capabilities and the parameters zebcast actually committed.
      </p>

      {/* DLNA side-by-side */}
      {dlnaProtoInfo && (
        <DiffRow
          label="DLNA"
          left={{
            heading: 'TV advertised (GetProtocolInfo)',
            content: (
              <>
                <div><b>{dlnaProtoInfo.props.mime_count}</b> MIME types</div>
                <div className="font-mono text-[10px] text-gray-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {(dlnaProtoInfo.props.advertised_mimes || []).join(', ')}
                </div>
              </>
            ),
          }}
          right={{
            heading: 'Zebcast set (SetAVTransportURI)',
            content: dlnaSetUri ? (
              <>
                <div>MIME: <b className="font-mono">{dlnaSetUri.props.mime_set}</b></div>
                <div className="text-[11px] text-gray-700 mt-1">
                  Source MIME: <span className="font-mono">{dlnaSetUri.props.source_mime || '—'}</span>
                </div>
                <div className="text-[11px] mt-1">
                  Sink supports our MIME?{' '}
                  <span className={dlnaSetUri.props.mime_supported_by_sink ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                    {dlnaSetUri.props.mime_supported_by_sink ? 'yes' : 'no'}
                  </span>
                </div>
                {dlnaSetUriResp && (
                  <div className="text-[11px] mt-1">
                    Result:{' '}
                    <span className={dlnaSetUriResp.props.ok ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                      {dlnaSetUriResp.props.ok ? 'accepted' : `rejected (${dlnaSetUriResp.props.error_message})`}
                    </span>
                  </div>
                )}
              </>
            ) : <span className="italic text-gray-500">SetAVTransportURI not recorded</span>,
          }}
        />
      )}

      {/* Miracast WFD side-by-side */}
      {wfdGetParam && (
        <DiffRow
          label="Miracast"
          left={{
            heading: 'TV advertised (M3 GET_PARAMETER)',
            content: (
              <>
                <div className="text-[11px]">
                  Capability params requested: <b>{(wfdGetParam.props.body_keys || []).length}</b>
                </div>
                <div className="font-mono text-[10px] text-gray-700 mt-1 max-h-32 overflow-y-auto">
                  {(wfdGetParam.props.body_keys || []).join('\n')}
                </div>
              </>
            ),
          }}
          right={{
            heading: 'Zebcast set (M4 SET_PARAMETER + session_ready)',
            content: (
              <>
                {wfdSetParamCap && (
                  <div className="text-[11px]">
                    M4 params: <b>{(wfdSetParamCap.props.body_keys || []).length}</b>
                  </div>
                )}
                {wfdSessionReady && (
                  <>
                    <div className="text-[11px] mt-1">
                      Video: <span className="font-mono">{wfdSessionReady.props.video_format}</span>
                    </div>
                    <div className="text-[11px]">
                      Audio: <span className="font-mono">{wfdSessionReady.props.audio_format}</span>
                    </div>
                    <div className="text-[11px]">
                      RTP port: <span className="font-mono">{wfdSessionReady.props.client_rtp_port}</span>
                    </div>
                  </>
                )}
                {!wfdSessionReady && (
                  <div className="text-red-700 text-[11px] italic mt-1">
                    Session never reached "ready" — negotiation didn't complete
                  </div>
                )}
              </>
            ),
          }}
        />
      )}

      {/* Chromecast side-by-side */}
      {ccLoad && (
        <DiffRow
          label="Chromecast"
          left={{
            heading: 'Zebcast declared',
            content: (
              <>
                <div>MIME: <b className="font-mono">{ccLoad.props.content_type}</b></div>
                <div>Stream: <b className="font-mono">{ccLoad.props.stream_type}</b></div>
                <div className="text-[11px] text-gray-700 truncate" title={ccLoad.props.uri}>
                  URI: <span className="font-mono">{ccLoad.props.uri}</span>
                </div>
              </>
            ),
          }}
          right={{
            heading: 'TV verdict',
            content: ccResp ? (
              <>
                <div>
                  Result:{' '}
                  <span className={ccResp.props.ok ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                    {ccResp.props.ok ? 'accepted' : 'rejected'}
                  </span>
                </div>
                {!ccResp.props.ok && (
                  <>
                    <div className="text-[11px]">Reason: <span className="font-mono">{ccResp.props.reason || '—'}</span></div>
                    <div className="text-[11px]">Type: <span className="font-mono">{ccResp.props.type || '—'}</span></div>
                  </>
                )}
                <div className="text-[11px] text-gray-500">
                  Elapsed: {ccResp.props.elapsed_ms}ms
                </div>
              </>
            ) : <span className="italic text-gray-500">no response recorded</span>,
          }}
        />
      )}

      {/* Transcode side-by-side */}
      {tcDecision && (
        <DiffRow
          label="Transcode"
          left={{
            heading: 'Source media',
            content: (
              <>
                <div className="text-[11px] truncate" title={tcDecision.props.source_name}>
                  Name: <span className="font-mono">{tcDecision.props.source_name}</span>
                </div>
                <div className="text-[11px]">
                  Size: <b>{((tcDecision.props.source_size_bytes || 0) / (1024 * 1024)).toFixed(1)} MB</b>
                </div>
                <div className="text-[11px]">
                  MIME: <span className="font-mono">{tcDecision.props.source_mime || '—'}</span>
                </div>
              </>
            ),
          }}
          right={{
            heading: 'Encoder output',
            content: (
              <>
                <div className="text-[11px]">
                  vcodec: <b className="font-mono">{tcDecision.props.target_vcodec}</b> @{' '}
                  {tcDecision.props.target_width}×{tcDecision.props.target_height} @{' '}
                  {tcDecision.props.target_bitrate_kbps}kbps
                </div>
                <div className="text-[11px]">
                  acodec: <b className="font-mono">{tcDecision.props.target_acodec}</b> ×{' '}
                  {tcDecision.props.target_audio_channels}ch
                </div>
                <div className="text-[11px]">
                  Muxer: <span className="font-mono">{tcDecision.props.target_muxer}</span>
                </div>
                <div className="text-[11px] mt-1">
                  Outcome:{' '}
                  {tcCompleted ? (
                    <span className="text-green-700 font-medium">
                      completed · {((tcCompleted.props.final_time_ms || 0) / 1000).toFixed(1)}s of{' '}
                      {((tcCompleted.props.final_length_ms || 0) / 1000).toFixed(1)}s
                    </span>
                  ) : tcError ? (
                    <span className="text-red-700 font-medium">
                      error at {((tcError.props.time_at_error_ms || 0) / 1000).toFixed(1)}s
                    </span>
                  ) : (
                    <span className="italic text-gray-500">in progress</span>
                  )}
                </div>
              </>
            ),
          }}
        />
      )}
    </div>
  );
}

function DiffRow({ label, left, right }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="inline-block bg-gray-700 text-white px-2 py-0.5 rounded text-xs font-medium mb-2">
        {label}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-blue-800 font-medium mb-1">
            {left.heading}
          </div>
          <div className="text-xs text-gray-900">{left.content}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-medium mb-1">
            {right.heading}
          </div>
          <div className="text-xs text-gray-900">{right.content}</div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// TransactionLogPanel — renders the per-protocol transaction log emitted
// by SessionTracker. Groups entries by `layer` (wfd_p2p, wfd_rtsp,
// wfd_streamer, dlna_http, …) and flattens each layer into a chronological
// row sequence with rel_ms timing.
//
// Each row:
//   [+12.4s]  wfd_rtsp  M3  GET_PARAMETER       in   200   42 keys
//                                              ⌄ wfd_video_formats: 00 00 02 02 …
//
// Body rows (layer ending in "_body") are stacked inline under their
// metadata row when expanded.
// ────────────────────────────────────────────────────────────────────────
function TransactionLogPanel({ entries }) {
  const [expanded, setExpanded] = React.useState(new Set());
  const toggle = (i) => {
    const s = new Set(expanded);
    s.has(i) ? s.delete(i) : s.add(i);
    setExpanded(s);
  };

  // Pair body entries with their metadata sibling so each metadata row
  // has the option to expand inline. Match key = layer.replace(_body,'')
  // + cseq + direction (close enough for RTSP — DLNA pairs by SOAP action).
  const bodyMap = new Map();
  for (const e of entries) {
    if (!e.layer || !e.layer.endsWith('_body')) continue;
    const base = e.layer.replace('_body', '');
    const cseq = e.props?.cseq;
    const dir = e.props?.direction;
    const key = `${base}|${cseq ?? ''}|${dir ?? ''}|${e.type}`;
    bodyMap.set(key, e);
  }
  const metaEntries = entries.filter(e => !e.layer || !e.layer.endsWith('_body'));

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-bold text-gray-900 mb-1">Transaction log</h3>
      <p className="text-xs text-gray-500 mb-4">
        Per-protocol low-level events. Click any row to expand the full body when available
        ({entries.length} entries · {bodyMap.size} with bodies).
      </p>
      <div className="space-y-1">
        {metaEntries.map((e, i) => {
          const layer = e.layer || '?';
          const cseq = e.props?.cseq;
          const dir = e.props?.direction;
          const key = `${layer}|${cseq ?? ''}|${dir ?? ''}|${e.type}`;
          const body = bodyMap.get(key);
          const isExpanded = expanded.has(i);

          const layerColor = {
            wfd_p2p:        'bg-purple-100 text-purple-800',
            wfd_rtsp:       'bg-blue-100 text-blue-800',
            wfd_streamer:   'bg-cyan-100 text-cyan-800',
            dlna_http:      'bg-emerald-100 text-emerald-800',
            dlna_soap:      'bg-emerald-100 text-emerald-800',
            cc_load:        'bg-orange-100 text-orange-800',
            cc_hls:         'bg-orange-100 text-orange-800',
            dms_browse:     'bg-fuchsia-100 text-fuchsia-800',
            dms_stream:     'bg-fuchsia-100 text-fuchsia-800',
            transcode_decision: 'bg-amber-100 text-amber-800',
          }[layer] || 'bg-gray-100 text-gray-700';

          const rel = e.rel_ms != null ? `+${(e.rel_ms / 1000).toFixed(1)}s` : '';
          const status = e.props?.status_code;
          const phase = e.props?.m_phase;
          const dirArrow = dir === 'in' ? '←' : dir === 'out' ? '→' : '';

          return (
            <div key={i}>
              <div
                onClick={() => body && toggle(i)}
                className={`flex items-center gap-3 text-xs font-mono py-1.5 px-2 rounded ${
                  body ? 'cursor-pointer hover:bg-yellow-50' : ''
                }`}>
                <span className="text-gray-400 w-14 text-right">{rel}</span>
                <span className={`px-2 py-0.5 rounded font-medium ${layerColor} w-32 text-center`}>{layer}</span>
                {phase && <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[10px] font-bold">{phase}</span>}
                {dirArrow && <span className="text-gray-500 w-4">{dirArrow}</span>}
                <span className="text-gray-900 font-medium flex-1 truncate">{e.type}</span>
                {cseq != null && <span className="text-gray-500">cseq={cseq}</span>}
                {status != null && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    status >= 200 && status < 300 ? 'bg-green-100 text-green-800' :
                    status >= 400               ? 'bg-red-100 text-red-800' :
                                                  'bg-gray-100 text-gray-700'
                  }`}>{status}</span>
                )}
                {e.props?.body_size_bytes != null && (
                  <span className="text-gray-400 w-16 text-right">{e.props.body_size_bytes}B</span>
                )}
                {body && <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>}
              </div>
              {isExpanded && body && (
                <pre className="ml-20 mb-2 bg-gray-900 text-green-200 text-[11px] rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
{body.props?.body || body.props?.response || JSON.stringify(body.props, null, 2)}
                </pre>
              )}
              {!body && Object.keys(e.props || {}).filter(k => !['cseq','direction','m_phase','status_code','body_size_bytes','elapsed_ms','method','uri'].includes(k)).length > 0 && (
                <div className="ml-20 mb-1 text-[10px] text-gray-500 font-mono">
                  {Object.entries(e.props).filter(([k]) => !['cseq','direction','m_phase','status_code','body_size_bytes','elapsed_ms','method','uri'].includes(k)).map(([k,v]) => (
                    <span key={k} className="mr-3">{k}={typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
